import type { FragoSummary, OrderFieldSource, OrderStatus, RotationOpo, RotationSummary } from "../../api/types";
import { k, t } from "../../i18n/i18n";

/** Parcours de la rotation CENTAC dans la vue de l'ordre (demande d'Alexis du 26/10/2026 : « revois l'UX/UI,
 *  simplifie car le parcours utilisateur n'est pas clair ») : une frise unique de tous les documents de la
 *  rotation dans l'ordre chronologique (OPO N°1 → FRAGO N°12… → résultats → WINGO N°21 → OPO N°2 … → résultats
 *  de l'OPO N°4), chacun avec son état, et une seule action attendue à la fois : la première étape qui n'est pas
 *  faite. Le serveur donne, OPO par OPO, la prochaine action (`next_action`, registre A114) ; le WINGO est un
 *  document généré puis diffusé (registre A120). Les FRAGO restent facultatifs : dans la frise après leur OPO,
 *  jamais une étape. */

export type StepKind = "numero" | "rediger" | "valider" | "saisir" | "wingo" | "diffuser";
export type StepState = "fait" | "courant" | "a_venir";

export interface Step {
  /** Rang dans le parcours (1 à 15). */
  rank: number;
  opo: number;
  kind: StepKind;
  state: StepState;
}

/** État d'un WINGO : document généré (« draft ») ou diffusé (« finalized ») ; un WINGO appliqué avant les WINGO
 *  documents compte comme généré. */
export function wingoStatus(opo: RotationOpo | undefined): OrderStatus | null {
  if (!opo) return null;
  return opo.wingo_status ?? (opo.wingo_applied ? "draft" : null);
}

function stepDone(kind: StepKind, opo: RotationOpo | undefined): boolean {
  if (!opo) return false;
  switch (kind) {
    case "numero":
      return true; // résolu par `rotationSteps` (numéro de la rotation saisi)
    case "rediger":
      return opo.order_status !== null;
    case "valider":
      return opo.order_status === "finalized";
    case "saisir":
      return opo.captured;
    case "wingo":
      return wingoStatus(opo) !== null;
    case "diffuser":
      return wingoStatus(opo) === "finalized";
  }
}

/** Les étapes de la rotation, dans l'ordre : le numéro de la rotation jouée d'abord s'il manque (registre A119 :
 *  il nomme la mission et les ordres ; étape préalable, hors des quinze), rédiger l'OPO N°1 (les suivants sont rédigés par le WINGO), puis
 *  pour chaque OPO : valider, saisir les résultats, générer puis diffuser le WINGO qui annonce l'OPO suivant
 *  (pas après l'OPO N°4). L'étape courante est la première qui n'est pas faite. */
export function rotationSteps(summary: RotationSummary): Step[] {
  const steps: Omit<Step, "rank" | "state">[] = [];
  // Numéro absent (null) ; un serveur antérieur ne le dit pas (undefined) : rien n'est demandé.
  const needsNumber = summary.rotation === null && summary.opos[0]?.order_status === null;
  if (needsNumber) steps.push({ opo: 1, kind: "numero" });
  for (const opo of summary.opos) {
    if (opo.number === 1) steps.push({ opo: 1, kind: "rediger" });
    steps.push({ opo: opo.number, kind: "valider" }, { opo: opo.number, kind: "saisir" });
    if (opo.number < summary.opos.length) {
      steps.push({ opo: opo.number, kind: "wingo" }, { opo: opo.number, kind: "diffuser" });
    }
  }
  let current = false;
  const offset = needsNumber ? 0 : 1;
  return steps.map((step, i) => {
    let state: StepState = "a_venir";
    if (step.kind !== "numero" && stepDone(step.kind, summary.opos[step.opo - 1])) state = "fait";
    else if (!current) {
      state = "courant";
      current = true;
    }
    return { ...step, rank: i + offset, state };
  });
}

/** Nombre d'étapes du parcours (sans l'étape préalable du numéro de la rotation). */
export function stepCount(steps: Step[]): number {
  return steps.filter((s) => s.kind !== "numero").length;
}

/** Étape courante (aucune : rotation achevée). */
export function currentStep(steps: Step[]): Step | null {
  return steps.find((s) => s.state === "courant") ?? null;
}

// --- Frise ------------------------------------------------------------------------------------------------------

export type ItemKind = "opo" | "frago" | "results" | "wingo";
/** Pastille d'un document de la frise : à faire, en cours (en rédaction, généré), fait (validé, saisi, diffusé). */
export type ItemTone = "a_faire" | "en_cours" | "fait";

export interface TimelineItem {
  key: string;
  kind: ItemKind;
  /** OPO auquel le document se rattache (le WINGO : l'OPO qu'il clôt). */
  opo: number;
  /** Numéro du FRAGO ou du WINGO ; null pour l'OPO et ses résultats. */
  number: number | null;
  tone: ItemTone;
  /** État en clair (clé de traduction). */
  state: string;
}

export const OPO_STATES = { a_faire: k("à rédiger"), en_cours: k("en rédaction"), fait: k("validé") } as const;
export const FRAGO_STATES = { a_faire: k("en rédaction"), en_cours: k("en rédaction"), fait: k("validé") } as const;
export const RESULTS_STATES = { a_faire: k("à saisir"), en_cours: k("à saisir"), fait: k("saisis") } as const;
export const WINGO_STATES = { a_faire: k("à générer"), en_cours: k("généré"), fait: k("diffusé") } as const;

function tone(status: OrderStatus | null | undefined): ItemTone {
  if (status === "finalized") return "fait";
  return status ? "en_cours" : "a_faire";
}

/** Tous les documents de la rotation dans l'ordre chronologique : OPO, ses FRAGO, ses résultats, le WINGO qui
 *  le clôt ; puis l'OPO suivant. */
export function timelineItems(summary: RotationSummary, fragos?: FragoSummary): TimelineItem[] {
  const items: TimelineItem[] = [];
  for (const opo of summary.opos) {
    const n = opo.number;
    const opoTone = tone(opo.order_status);
    items.push({ key: `opo-${n}`, kind: "opo", opo: n, number: null, tone: opoTone, state: OPO_STATES[opoTone] });
    for (const f of fragos?.opos.find((o) => o.opo === n)?.fragos ?? []) {
      const fragoTone = f.status === "finalized" ? "fait" : "en_cours";
      items.push({ key: `frago-${f.number}`, kind: "frago", opo: n, number: f.number, tone: fragoTone, state: FRAGO_STATES[fragoTone] });
    }
    const resultsTone: ItemTone = opo.captured ? "fait" : "a_faire";
    items.push({ key: `results-${n}`, kind: "results", opo: n, number: null, tone: resultsTone, state: RESULTS_STATES[resultsTone] });
    if (n < summary.opos.length) {
      const wingoTone = tone(wingoStatus(opo));
      items.push({ key: `wingo-${n}`, kind: "wingo", opo: n, number: wingoNumber(n), tone: wingoTone, state: WINGO_STATES[wingoTone] });
    }
  }
  return items;
}

/** Libellé court d'un document de la frise. */
export function itemLabel(item: TimelineItem): string {
  switch (item.kind) {
    case "opo":
      return t("OPO N°{n}", { n: item.opo });
    case "frago":
      return t("FRAGO N°{n}", { n: item.number ?? 0 });
    case "results":
      return t("Résultats {n}", { n: item.opo });
    case "wingo":
      return t("WINGO N°{n}", { n: item.number ?? 0 });
  }
}

/** Document ouvert dans la vue de l'ordre. */
export interface Opened {
  kind: ItemKind;
  opo: number;
  number: number | null;
}

export function itemKey(opened: Opened): string {
  switch (opened.kind) {
    case "opo":
      return `opo-${opened.opo}`;
    case "frago":
      return `frago-${opened.number}`;
    case "results":
      return `results-${opened.opo}`;
    case "wingo":
      return `wingo-${opened.opo}`;
  }
}

/** Document sur lequel se fait l'étape. */
export function stepDocument(step: Step): Opened {
  switch (step.kind) {
    case "numero":
    case "rediger":
    case "valider":
      return { kind: "opo", opo: step.opo, number: null };
    case "saisir":
      return { kind: "results", opo: step.opo, number: null };
    case "wingo":
    case "diffuser":
      return { kind: "wingo", opo: step.opo, number: wingoNumber(step.opo) };
  }
}

// --- Action attendue --------------------------------------------------------------------------------------------

/** L'action unique de l'étape courante :
 *  - « numero » : saisir le numéro de la rotation dans les Données initiales (registre A119) ;
 *  - « rediger » : rédiger l'OPO (OPO N°1, ou OPO annoncé par un WINGO appliqué sans réécriture) ;
 *  - « mettre_a_jour » : réécrire l'OPO depuis le WINGO appliqué ;
 *  - « valider » : valider l'OPO ouvert ; « relire » : l'ouvrir d'abord (on ne valide pas un ordre sans l'avoir lu) ;
 *  - « saisir » : ouvrir la saisie des résultats ; « enregistrer » : enregistrer la saisie ouverte ;
 *  - « generer » : générer le WINGO (et l'ouvrir) ;
 *  - « diffuser » : diffuser le WINGO ouvert ; « relire_wingo » : l'ouvrir d'abord. */
export type StepAction =
  | "numero"
  | "rediger"
  | "mettre_a_jour"
  | "valider"
  | "relire"
  | "saisir"
  | "enregistrer"
  | "generer"
  | "diffuser"
  | "relire_wingo";

export function stepAction(step: Step, summary: RotationSummary, opened: Opened): StepAction {
  const here = itemKey(opened) === itemKey(stepDocument(step));
  const next = summary.opos[step.opo - 1]?.next_action;
  switch (step.kind) {
    case "numero":
      return "numero";
    case "rediger":
      return "rediger";
    case "valider":
      if (next === "rediger") return "rediger";
      if (next === "regenerer") return "mettre_a_jour";
      return here ? "valider" : "relire";
    case "saisir":
      return here ? "enregistrer" : "saisir";
    case "wingo":
      return "generer";
    case "diffuser":
      return here ? "diffuser" : "relire_wingo";
  }
}

/** Libellé explicite du bouton de l'action attendue. */
export function actionLabel(action: StepAction, step: Step): string {
  const n = step.opo;
  const w = wingoNumber(n);
  switch (action) {
    case "numero":
      return t("Saisir le numéro de la rotation");
    case "rediger":
      return t("Rédiger l'OPO N°{n}", { n });
    case "mettre_a_jour":
      return t("Mettre à jour l'OPO N°{n} depuis le WINGO N°{w}", { n, w: wingoNumber(n - 1) });
    case "valider":
      return t("Valider l'OPO N°{n}", { n });
    case "relire":
      return t("Relire l'OPO N°{n}", { n });
    case "saisir":
      return t("Saisir les résultats de l'OPO N°{n}", { n });
    case "enregistrer":
      return t("Enregistrer les résultats de l'OPO N°{n}", { n });
    case "generer":
      return t("Générer le WINGO N°{w}", { w });
    case "diffuser":
      return t("Diffuser le WINGO N°{w}", { w });
    case "relire_wingo":
      return t("Relire le WINGO N°{w}", { w });
  }
}

/** Titre de l'étape (ce qu'il faut faire), une ligne. */
export function stepTitle(step: Step): string {
  const n = step.opo;
  switch (step.kind) {
    case "numero":
      return t("Saisir le numéro de la rotation jouée");
    case "rediger":
      return t("Rédiger l'OPO N°{n}", { n });
    case "valider":
      return n === 1 ? t("Relire et valider l'OPO N°{n}", { n }) : t("Relire et valider l'OPO N°{n}, rédigé par le WINGO N°{w}", { n, w: wingoNumber(n - 1) });
    case "saisir":
      return t("Saisir les résultats de l'OPO N°{n}", { n });
    case "wingo":
      return t("Générer le WINGO N°{w}, qui rédige l'OPO N°{next}", { w: wingoNumber(n), next: n + 1 });
    case "diffuser":
      return t("Relire et diffuser le WINGO N°{w}", { w: wingoNumber(n) });
  }
}

/** Ce que fait l'étape, en une phrase. */
export function stepDetail(step: Step): string {
  const n = step.opo;
  switch (step.kind) {
    case "numero":
      return t("Par exemple 08/26, dans les Données initiales : il nomme le dossier, sa mission et tous les ordres de la rotation.");
    case "rediger":
      return t("Rédigé d'après la rotation type du CENTAC et la carte ; vous le relirez ensuite page par page.");
    case "valider":
      return t("Validé, il est diffusé et joué : il ne change plus. Un FRAGO pourra le modifier en cours d'exécution.");
    case "saisir":
      return t("Bilan de fin de phase : CAPOPS de l'EDC et de l'EEI et statut de la mission ; le reste est facultatif.");
    case "wingo":
      return t("Ordre d'avertissement tiré des résultats : bascules des missions selon la CAPOPS ; il rédige l'OPO N°{next}.", { next: n + 1 });
    case "diffuser":
      return t("Diffusé, il est figé et rejoint le dossier d'exercice après l'OPO N°{n}.", { n });
  }
}

/** Source du texte d'un paragraphe (éditeur de l'ordre CENTAC), en mots simples. */
export const SOURCE_LABELS: Record<OrderFieldSource, string> = {
  gabarit: k("Repris de la rotation type du CENTAC"),
  carte: k("Tiré de la carte"),
  gabarit_carte: k("Rotation type du CENTAC complétée d'après la carte"),
  saisie: k("Saisi par vous (Données initiales)"),
  calendrier: k("Calendrier de la rotation, d'après la date de début saisie"),
  wingo: k("Mis à jour depuis le WINGO, d'après les résultats saisis"),
  // Paragraphe Météo : aucun réglage météo en CENTAC, la saison du camp se retouche dans l'ordre.
  saison: k("Météo de saison du camp à la date de début, à retoucher ici"),
};

/** Numéro du WINGO rédigé à la fin de l'OPO `opo` (21, 31, 41). */
export function wingoNumber(opo: number): number {
  return (opo + 1) * 10 + 1;
}

/** OPO dont un FRAGO peut être rédigé maintenant (OPO validé, aucun FRAGO en rédaction) : numéro du FRAGO, sinon
 *  null. */
export function nextFrago(summary: FragoSummary | undefined, opo: number): number | null {
  const entry = summary?.opos.find((o) => o.opo === opo);
  return entry && entry.blocked === null ? entry.next_number : null;
}
